import React, { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, MenuItem } from "@mui/material";
import PropTypes from "prop-types";

import { systemsService } from "../../../../services/systems-service";
import { integrationService } from "../../../../services/integration-service";
import { busynessProcessesService } from "../../../../services/busyness-processes-service.js";

import { SelectInput, TextInput } from "../../../common/form-controls";
import { SearchHelpDescriptionInput } from "../../../common/form-controls/search-help-input.jsx";
import SearchHelpDialog from "../../../common/search-helps";
import { ADD_MODE, EntityFormSubtitle, READ_MODE } from "../../../common/entity-form";

import FunctionFormPageViews from "./function-form-page-views.jsx";
import { SYSTEM_TYPES } from "../../../../utils/integration-utils.js";
import { getSystemsExtended, getBusynessProcessesSearchHelp } from "../../../../utils/searchHelps";
import { FUNCTION_TYPES } from "./const.js";


const FunctionIdInput = ({ id, setId, disabled, error }) => {
    const intl = useIntl();

    return (
        <TextInput
            label={intl.formatMessage({ id: "function-form-page-function.input-function-id" })}
            value={id}
            onChange={(event) => setId(event.target.value)}
            disabled={disabled}
            error={error?.value === id}
            errorMessage={error?.message}
        />
    );
};


const FunctionDescriptionInput = ({ description, setDescription, disabled, error }) => {
    const intl = useIntl();

    return (
        <TextInput
            multiline
            rows={3}
            label={intl.formatMessage({ id: "function-form-page-function.input-description" })}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={disabled}
            error={error?.value === description}
            errorMessage={error?.message}
        />
    );
};

const SystemInput = ({ systemId, setSystemId, disabled, error, description }) => {
    const intl = useIntl();

    const title = useMemo(
        () => intl.formatMessage({ id: "function-form-page-function.input-system" }),
        [intl]
    );

    const columns = useMemo(
        () => [
            { id: "key", title: intl.formatMessage({ id: "search-help.columns.system" }) },
            { id: "text", title: intl.formatMessage({ id: "search-help.columns.description" }) }
        ],
        [intl]
    );

    const [open, setOpen] = useState(false);

    return (
        <>
            <SearchHelpDescriptionInput
                label={title}
                value={systemId}
                error={error ? systemId === error.value : false}
                errorMessage={error && error.message}
                disabled={disabled}
                onChange={(event) => setSystemId(event.target.value)}
                onOpenSearchHelp={() => { setOpen(true) }}
                description={description}
                className="function-form-page-function-type-input"
            />

            <SearchHelpDialog
                defaultSearchString={systemId}
                title={title}
                columns={columns}
                open={open}
                readRows={getSystemsExtended}
                onSubmit={(row) => {
                    setSystemId(row.key);
                    setOpen(false);
                }}
                onCancel={() => {
                    setOpen(false)
                }}
                selectOneRow={true}
            />
        </>
    );
};

const FunctionTypeInput = ({ functionType, setFunctionType, disabled, error }) => {
    const intl = useIntl();

    const items = useMemo(
        () => {
            return [{
                id: FUNCTION_TYPES.OP,
                text: intl.formatMessage({ id: "function-form-page-function.input-function-type.op" })
            }, {
                id: FUNCTION_TYPES.ROLE,
                text: intl.formatMessage({ id: "function-form-page-function.input-function-type.role" })
            }]
        },
        [intl]
    );

    return (
        <SelectInput
            label={intl.formatMessage({ id: "function-form-page-function.input-function-type.title" })}
            value={functionType}
            onChange={(event) => setFunctionType(event.target.value)}
            error={error ? functionType === error.value : false}
            errorMessage={error && error.message}
            disabled={disabled}
            className="function-form-page-function-type-input"
        >
            {items.map(item => (
                <MenuItem
                    value={item.id}
                    key={item.id}
                >
                    {item.text}
                </MenuItem>
            ))}
        </SelectInput>
    );
};


const BusynessProcessInput = ({ busynessProcessId, setBusynessProcessId, disabled, error, description }) => {
    const intl = useIntl();

    const title = useMemo(
        () => intl.formatMessage({ id: "function-form-page-function.input-busyness-process" }),
        [intl]
    );

    const columns = useMemo(
        () => [
            { id: "key", title: intl.formatMessage({ id: "search-help.columns.busyness-process" }) },
            { id: "text", title: intl.formatMessage({ id: "search-help.columns.description" }) }
        ],
        [intl]
    );

    const [open, setOpen] = useState(false);

    return (
        <>
            <SearchHelpDescriptionInput
                label={title}
                value={busynessProcessId}
                error={error ? (busynessProcessId === error.value || error.value === null) : false}
                errorMessage={error && error.message}
                disabled={disabled}
                onChange={(event) => setBusynessProcessId(event.target.value)}
                onOpenSearchHelp={() => { setOpen(true) }}
                description={description}
            />

            <SearchHelpDialog
                defaultSearchString={busynessProcessId}
                title={title}
                columns={columns}
                open={open}
                readRows={getBusynessProcessesSearchHelp}
                onSubmit={(row) => {
                    setBusynessProcessId(row.key);
                    setOpen(false);
                }}
                onCancel={() => {
                    setOpen(false)
                }}
                selectOneRow={true}
            />
        </>
    );
};

const FunctionFormPageFunction = ({
    form,
    mode,
    setForm,
    validationErrors,
    setValidationErrors,
    errorScroll,
    setErrorScroll
}) => {
    const setId = value => setForm({ ...form, id: value });
    const setDescription = value => setForm({ ...form, description: value });

    const setSystemType = value => setForm(form => {
        const systemTypeChanged = form.systemType !== value
        const prevSystemType = form.systemType

        let newOperations = []
        let newPermissions = []
        let newRoles = []
        let functionType = form.functionType

        if (!systemTypeChanged) {
            return { ...form, systemType: value }
        }

        if (prevSystemType === SYSTEM_TYPES.SAP) {
            form.operationsSAPBuffer = form.operations
            form.permissionsSAPBuffer = form.permissions
        }

        if (prevSystemType === SYSTEM_TYPES.ONE_C) {
            form.operations1CBuffer = form.operations
            form.permissions1CBuffer = form.permissions
        }

        if (prevSystemType === SYSTEM_TYPES.OTHER) {
            form.rolesBuffer = form.roles
        }

        if (value === SYSTEM_TYPES.ONE_C) {
            functionType = FUNCTION_TYPES.OP

            newOperations = form.operations1CBuffer ? form.operations1CBuffer : []
            newPermissions = form.permissions1CBuffer ? form.permissions1CBuffer : []
        }
        if (value === SYSTEM_TYPES.SAP) {
            functionType = FUNCTION_TYPES.OP

            newOperations = form.operationsSAPBuffer ? form.operationsSAPBuffer : []
            newPermissions = form.permissionsSAPBuffer ? form.permissionsSAPBuffer : []
        }
        if (value === SYSTEM_TYPES.OTHER) {
            functionType = FUNCTION_TYPES.ROLE

            newRoles = form.rolesBuffer ? form.rolesBuffer : []
        }

        return { ...form, systemType: value, operations: newOperations, permissions: newPermissions, functionType: functionType, roles: newRoles }
    });

    const setFunctionType = value => setForm(form => {
        const funcTypeChanged = form.functionType !== value

        if (!funcTypeChanged) {
            return { ...form, functionType: value }
        }

        return { ...form, functionType: value }
    })

    const setSystemId = async (value) => {
        let systemError;
        let system;
        let isGroup;
        let systemText = "";

        if (value) {
            try {
                system = await systemsService.getSystemExtendedDetailed(value);
                isGroup = system.is_group
                systemText = system.text
                setSystemType(system.system_type)
            } catch (e) {
                systemError = e;
            }
        }

        const newSystemId = system?.key ?? value;

        let textMap;

        if (!systemError) {
            try {
                const transactions = form.operations.map(item => item.operation)
                textMap = await integrationService.getTransactionsTextsMass(newSystemId, transactions, isGroup);
            } catch (e) {
                console.error(e);
            }
        }

        setForm(form => {
            const newOperations = form.operations.map(operation => ({
                ...operation,
                description: textMap?.[operation.operation] ?? ""
            }));

            return {
                ...form,
                systemId: newSystemId,
                systemText: systemText,
                isGroup: isGroup,
                operations: newOperations
            }
        });

        setValidationErrors(errors => {
            const newErrors = { ...errors };

            if (systemError) {
                newErrors.system_id = {
                    value: newSystemId,
                    message: systemError.detail ?? systemError.title
                };
            } else {
                delete newErrors.system_id;
            }

            return newErrors;
        });
    };


    const setBusynessProcessId = async (value) => {
        let processError;
        let process;
        let processText = "";

        if (value) {
            try {
                process = await busynessProcessesService.getBusynessProcessDetailed(value);
                processText = process.description
            } catch (e) {
                processError = e;
            }
        }

        const newProcessId = process?.id ?? value;

        setForm(form => {
            return {
                ...form,
                process: newProcessId,
                processText: processText,
            }
        });

        setValidationErrors(errors => {
            const newErrors = { ...errors };

            if (processError) {
                newErrors.business_process_id = {
                    value: newProcessId,
                    message: processError.detail ?? processError.title
                };
            } else {
                delete newErrors.business_process_id;
            }

            return newErrors;
        });
    };

    const formDisabled = mode === READ_MODE;
    const idDisabled = mode !== ADD_MODE;
    const viewsDisabled = formDisabled || !form.systemId || validationErrors?.system_id;

    return (
        <>
            <EntityFormSubtitle className="function-form-page-function-subtitle">
                <FormattedMessage id="function-form-page-function.title" />
            </EntityFormSubtitle>

            <Box className="function-form-page-function-inputs">
                <FunctionIdInput
                    id={form.id}
                    setId={setId}
                    disabled={idDisabled}
                    error={validationErrors && validationErrors["function_id"]}
                />

                <FunctionDescriptionInput
                    description={form.description}
                    setDescription={setDescription}
                    disabled={formDisabled}
                    error={validationErrors && validationErrors["description"]}
                />

                <BusynessProcessInput
                    busynessProcessId={form.process}
                    setBusynessProcessId={setBusynessProcessId}
                    disabled={formDisabled}
                    error={validationErrors && validationErrors["business_process_id"]}
                    description={form.processText}
                />

                <SystemInput
                    systemId={form.systemId}
                    setSystemId={setSystemId}
                    disabled={formDisabled}
                    error={validationErrors && validationErrors["system_id"]}
                    description={form.systemText}
                />

                <FunctionTypeInput
                    functionType={form.functionType}
                    setFunctionType={setFunctionType}
                    error={validationErrors && validationErrors["function_type"]}
                    disabled={form.systemType === SYSTEM_TYPES.OTHER || formDisabled}
                />
            </Box>

            <FunctionFormPageViews
                form={form}
                setForm={setForm}
                formDisabled={viewsDisabled}
                validationErrors={validationErrors}
                errorScroll={errorScroll}
                setErrorScroll={setErrorScroll}
            />
        </>
    );
};

FunctionFormPageFunction.propTypes = {
    form: PropTypes.object,
    mode: PropTypes.string,
    setForm: PropTypes.func,
    validationErrors: PropTypes.object,
    setValidationErrors: PropTypes.func,
    errorScroll: PropTypes.object,
    setErrorScroll: PropTypes.func
};

FunctionIdInput.propTypes = {
    id: PropTypes.string,
    setId: PropTypes.func,
    disabled: PropTypes.bool,
    error: PropTypes.object
};

FunctionDescriptionInput.propTypes = {
    description: PropTypes.string,
    setDescription: PropTypes.func,
    disabled: PropTypes.bool,
    error: PropTypes.object
};

SystemInput.propTypes = {
    systems: PropTypes.arrayOf(PropTypes.object),
    systemId: PropTypes.string,
    setSystemId: PropTypes.func,
    disabled: PropTypes.bool,
    error: PropTypes.object,
    description: PropTypes.string,
};

BusynessProcessInput.propTypes = {
    busynessProcessId: PropTypes.string,
    setBusynessProcessId: PropTypes.func,
    disabled: PropTypes.bool,
    error: PropTypes.object,
    description: PropTypes.string,
}

FunctionTypeInput.propTypes = {
    functionType: PropTypes.string,
    setFunctionType: PropTypes.func,
    disabled: PropTypes.bool,
    error: PropTypes.object,
}

export default FunctionFormPageFunction;
